import { Field, Input, Select } from 'iboon-ui';

import type { CategoryField } from './api';

/**
 * The inputs a category asks for, built from its own schema.
 *
 * Until this existed, `fieldSchema` was stored, and rendered on the detail
 * page, and there was no way to put a value into it -- so every asset showed
 * an empty list of exactly the details the category said mattered. The schema
 * described a form nobody could fill in.
 *
 * Values are held as a plain object keyed by `field.key`, which is what the
 * `fields` jsonb column stores, so nothing has to be translated on the way in
 * or out.
 */
export function CustomFields({
  schema,
  values,
  onChange,
}: {
  schema: CategoryField[];
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  if (schema.length === 0) return null;

  const set = (key: string, value: unknown) => onChange({ ...values, [key]: value });

  return (
    <>
      {schema.map((field) => {
        const current = values[field.key];
        const text = current === undefined || current === null ? '' : String(current);

        return (
          <Field key={field.key} label={field.label} required={field.required}>
            {field.type === 'select' ? (
              <Select value={text} onChange={(event) => set(field.key, event.target.value)}>
                {/* Blank first: a select with no empty option silently records
                    its first value for everything nobody touched. */}
                <option value="">—</option>
                {(field.options ?? []).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                value={text}
                onChange={(event) => {
                  const raw = event.target.value;
                  // Numbers are stored as numbers, so the detail page and any
                  // later report can compare them rather than sorting "10"
                  // before "9". An empty box stays empty rather than becoming 0.
                  if (field.type === 'number') {
                    set(field.key, raw === '' ? '' : Number(raw));
                  } else {
                    set(field.key, raw);
                  }
                }}
              />
            )}
          </Field>
        );
      })}
    </>
  );
}
